import React, { useContext, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';

import { AccountKnowledgeContext } from './AccountKnowledgeProvider';
import SectionEditor from './SectionEditor';
import TestTriggersBox from './TestTriggersBox';
import { TIER_LABELS } from './constants';
import { HintPrefill } from './eval/buildHintSection';

// This account's own definitions. Grouped by WHEN the agent sees them, because
// that is the decision a staff author is actually making: every answer, or only
// when it is relevant.

const BodyDiv = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const NavDiv = styled.div`
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid var(--color-border-secondary);
  overflow-y: auto;
  padding: 16px 0;
`;

const RightDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const GroupDiv = styled.div`
  padding: 8px 16px 4px 16px;
`;

const ItemButton = styled.button<{ isSelected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  cursor: pointer;
  padding: 6px 16px;
  background-color: ${(props) =>
    props.isSelected ? 'var(--color-bg-secondary)' : 'transparent'};
`;

const CenteredDiv = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
`;

const TIERS: AccountKb.Tier[] = ['always', 'triggered'];

const KnowledgeTab = () => {
  const { sections, isLoading, rights, validation } = useContext(
    AccountKnowledgeContext,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // "Teach the agent" from a test lands here with a section prefill.
  const prefill = (location.state as { prefill?: HintPrefill } | null)?.prefill;
  const [isCreating, setIsCreating] = useState<boolean>(Boolean(prefill));

  // Proposals live in the Suggestions tab; this tab is what is live.
  const live = sections.filter((section) => section.status === 'active');
  const requestedId = searchParams.get('section');
  const selected = live.find((section) => section.id === requestedId);

  if (isLoading) {
    return (
      <CenteredDiv>
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Loading this account&apos;s knowledge…
        </Typography.Body>
      </CenteredDiv>
    );
  }

  return (
    <BodyDiv>
      <NavDiv data-testid="account-kb-section-list">
        <GroupDiv>
          <Button
            type="Secondary"
            size="Small"
            label="New section"
            isDisabled={!rights.canEdit}
            onClick={() => {
              setIsCreating(true);
              setSearchParams({});
            }}
          />
        </GroupDiv>
        {TIERS.filter((tier) =>
          live.some((section) => section.tier === tier),
        ).map((tier) => (
          <div key={tier}>
            <GroupDiv>
              <Typography.Body type="Label" color="var(--color-text-secondary)">
                {TIER_LABELS[tier]}
              </Typography.Body>
            </GroupDiv>
            {live
              .filter((section) => section.tier === tier)
              .map((section) => (
                <ItemButton
                  key={section.id}
                  type="button"
                  isSelected={section.id === selected?.id}
                  onClick={() => {
                    setIsCreating(false);
                    setSearchParams({ section: section.id });
                  }}
                >
                  <Row centerAlign spaceBetween>
                    <Typography.Body
                      type={
                        section.id === selected?.id ? 'Body 13 Bold' : 'Body 13'
                      }
                    >
                      {section.title}
                    </Typography.Body>
                    {section.proposedRev > 0 && (
                      <Badge badgeType="Blue" text="Change pending" />
                    )}
                  </Row>
                </ItemButton>
              ))}
          </div>
        ))}
        {live.length === 0 && (
          <GroupDiv>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              Nothing yet. Anything written here is what the agent knows about
              this customer specifically.
            </Typography.Body>
          </GroupDiv>
        )}
      </NavDiv>
      <RightDiv>
        {(isCreating || selected) && (
          <SectionEditor
            section={isCreating ? undefined : selected}
            initialDraft={isCreating ? prefill : undefined}
            onSaved={(sectionId) => {
              setIsCreating(false);
              setSearchParams({ section: sectionId });
            }}
          />
        )}
        {!isCreating && !selected && (
          <CenteredDiv>
            <Typography.Body type="Body 13" color="var(--color-text-secondary)">
              Pick a section, or add one.
            </Typography.Body>
          </CenteredDiv>
        )}
        {validation && validation.errors.length > 0 && (
          <GroupDiv>
            {validation.errors.map((issue) => (
              <Row
                key={`${issue.code}-${issue.sectionId ?? ''}`}
                centerAlign
                style={{ gap: 8 }}
              >
                <Badge badgeType="Red" text="Needs fixing" />
                <Typography.Body type="Body 12">
                  {issue.message}
                </Typography.Body>
              </Row>
            ))}
          </GroupDiv>
        )}
        <TestTriggersBox />
      </RightDiv>
    </BodyDiv>
  );
};

export default KnowledgeTab;
